<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<script lang="ts">
  import Panel from '@components/panel.svelte'
  import Image from '@components/image.svelte'
  import EntityLink from '@components/entity-link.svelte'
  import dayjs from 'dayjs'
  import {
    selectedId,
    selectedPlayer,
    lineScore,
    periodClock,
    plays,
  } from './stores'
  import {
    ordinalNumber,
    type GameraTeamReference,
    type NhlStatKey,
    type StatModel,
  } from '@statmuse/core/gamera'
  import Icon from '@components/icon.svelte'
  import { isNativeMobile } from '@lib/stores'
  import { onMount } from 'svelte'
  import { round, max, range, first } from 'lodash-es'
  import HockeyRink from './hockey-rink.svelte'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined
  export let gameTimestamp: string

  let selectedTab = 0

  $: firstPlayOfCurrentPeriod = first(
    $plays.filter((p) => p.period === $periodClock.period),
  )

  $: hasShootout = firstPlayOfCurrentPeriod?.clock === '00:00'

  $: selectorWidth = round(
    (1 / (max([3, $lineScore.away?.periods?.length]) + (hasShootout ? 2 : 1))) *
      100,
    1,
  )

  $: tabs = [
    { label: 'All', value: 0 },
    ...range(1, (max([3, $lineScore.away?.periods?.length]) ?? 3) + 1).map(
      (x) => ({
        label: `${x < 4 ? ordinalNumber(x) : x === 4 ? '' : x - 3}${
          x > 3 ? 'OT' : ''
        }`,
        value: x,
      }),
    ),
    ...(hasShootout
      ? [{ label: 'SO', value: $lineScore.away?.periods?.length + 1 }]
      : []),
  ]

  $: opponentTeam = $selectedId?.alignment === 'away' ? homeTeam : awayTeam
  $: team = $selectedId?.alignment === 'away' ? awayTeam : homeTeam

  type DisplayFn = (s?: StatModel<NhlStatKey>) => string

  $: statSections = (
    $selectedPlayer?.player?.position === 'CENTER' ||
    $selectedPlayer?.player?.position === 'RIGHT_WING' ||
    $selectedPlayer?.player?.position === 'LEFT_WING'
      ? [
          { key: 'G', statKey: 'Goals' },
          { key: 'A', statKey: 'Assists' },
          { key: 'P', statKey: 'Points' },
          { key: '+/-', statKey: 'PlusMinus' },
          { key: 'PIM', statKey: 'Penalty-Minutes' },
          { key: 'PPG', statKey: 'PowerPlay-Goals' },
          { key: 'PPA', statKey: 'PowerPlay-Assists' },
          { key: 'SHG', statKey: 'ShortHanded-Goals' },
          { key: 'SHA', statKey: 'ShortHanded-Assists' },
          { key: 'S', statKey: 'Shots' },
          { key: 'S%', statKey: 'ShootingPercentage' },
          { key: 'FOW', statKey: 'Faceoff-Wins' },
          { key: 'FO', statKey: 'Faceoff-Count' },
          { key: 'FOW%', statKey: 'Faceoff-WinPercentage' },
          { key: 'HIT', statKey: 'Hits' },
          { key: 'BKS', statKey: 'BlockedShots' },
          { key: 'TKS', statKey: 'Takeaways' },
          { key: 'GVS', statKey: 'Giveaways' },
          { key: 'TOI', statKey: 'TimeOnIce' },
        ]
      : $selectedPlayer?.player?.position === 'DEFENSEMAN'
      ? [
          { key: 'G', statKey: 'Goals' },
          { key: 'A', statKey: 'Assists' },
          { key: 'P', statKey: 'Points' },
          { key: '+/-', statKey: 'PlusMinus' },
          { key: 'PIM', statKey: 'Penalty-Minutes' },
          { key: 'PPG', statKey: 'PowerPlay-Goals' },
          { key: 'PPA', statKey: 'PowerPlay-Assists' },
          { key: 'SHG', statKey: 'ShortHanded-Goals' },
          { key: 'SHA', statKey: 'ShortHanded-Assists' },
          { key: 'HIT', statKey: 'Hits' },
          { key: 'BKS', statKey: 'BlockedShots' },
          { key: 'TKA', statKey: 'Takeaways' },
          { key: 'GVA', statKey: 'Giveaways' },
          { key: 'S', statKey: 'Shots' },
          { key: 'S%', statKey: 'ShootingPercentage' },
          { key: 'TOI', statKey: 'TimeOnIce' },
        ]
      : $selectedPlayer?.player?.position === 'GOALIE'
      ? [
          { key: 'W', statKey: 'Wins' },
          { key: 'L', statKey: 'Losses' },
          { key: 'OTL', statKey: 'OvertimeLosses' },
          { key: 'SA', statKey: 'ShotsAgainst' },
          { key: 'SV', statKey: 'Saves' },
          { key: 'SV%', statKey: 'SavePercentage' },
          { key: 'GA', statKey: 'GoalsAgainst' },
          { key: 'GAA', statKey: 'GoalsAgainstAverage' },
          { key: 'SHO', statKey: 'Shutouts' },
          { key: 'MIN', statKey: 'MinutesPlayed' },
        ]
      : []
  ) as {
    key: string
    statKey: NhlStatKey
    display?: DisplayFn
  }[]

  function onSwipe() {
    selectedId.set(undefined)
  }

  onMount(function () {
    document.addEventListener('swipe-right', onSwipe)
    document.addEventListener('swipe-left', onSwipe)
    return () => {
      document.removeEventListener('swipe-right', onSwipe)
      document.removeEventListener('swipe-left', onSwipe)
    }
  })
</script>

{#if $selectedPlayer}
  {@const { player, alignment, stats } = $selectedPlayer}
  <div
    class="fixed w-full h-full top-0 left-0 z-[100] bg-gray-6/80 dark:bg-gray-4/80 overflow-auto"
    on:click={() => selectedId.set(undefined)}
  >
    <div
      class="px-2 py-2 flex flex-col md:flex-row items-start gap-2 w-full md:w-fit absolute left-1/2 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2"
      class:top-2={!$isNativeMobile}
      class:top-10={$isNativeMobile}
      on:click|stopPropagation
    >
      <div class="flex flex-col gap-2 w-full md:w-[380px] md:-mt-11">
        <div
          class="w-fit p-2 bg-gray-8 dark:bg-gray-3 text-inherit border border-gray-6 dark:border-gray-3 rounded-full hover:cursor-pointer"
          on:click={() => selectedId.set(undefined)}
        >
          <Icon name="x" class="w-4 h-4" />
        </div>
        <Panel>
          <div
            class="flex items-center gap-1 border-b border-gray-6 dark:border-gray-4 -mx-3 px-3"
          >
            <Image
              class="h-[75px] max-w-[100px] select-none object-contain object-bottom"
              alt={player?.longName ?? ''}
              src={player?.media.bustOrSilhouette?.imageUrl ?? ''}
              width={180}
              height={180}
              loading="eager"
            />
            <div>
              <EntityLink
                entity={{
                  type: 'player',
                  id: player?.id,
                  display: player?.longName ?? '',
                  domain: 'NHL',
                }}
                class="whitespace-nowrap text-inherit"
              >
                {player?.longName ?? ''}
              </EntityLink>
              <div
                class="flex items-center justify-between gap-2 text-sm text-gray-5"
              >
                <EntityLink
                  entity={undefined}
                  class="whitespace-nowrap text-inherit"
                >
                  {dayjs(gameTimestamp).format('MMM D')}
                </EntityLink>
                <EntityLink
                  entity={undefined}
                  class="flex gap-1 items-center text-inherit"
                >
                  <p class="whitespace-nowrap">
                    {alignment === 'away' ? 'at' : 'vs'}
                  </p>
                  <div class="flex gap-1 items-center">
                    <Image
                      class="h-4 max-w-4 select-none object-contain object-center"
                      alt={opponentTeam?.name ?? ''}
                      src={opponentTeam?.logoImageUrl ?? ''}
                      width={16}
                      height={16}
                      loading="eager"
                    />

                    <p class="whitespace-nowrap">
                      {opponentTeam?.abbreviation ?? ''}
                    </p>
                  </div>
                </EntityLink>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap gap-3 py-3 leading-snug items-start">
            {#each statSections as section (section)}
              <div class="flex flex-col items-center">
                <div class="whitespace-nowrap mb-[2px]">
                  {#if section.display}
                    {section.display(stats)}
                  {:else}
                    {stats?.[section.statKey]?.display ?? '-'}
                  {/if}
                </div>
                <div
                  class="font-light text-sm opacity-80 whitespace-pre text-center leading-[1.1]"
                >
                  {section.key}
                </div>
              </div>
            {/each}
          </div>
        </Panel>
        {#if $selectedPlayer.events.length > 0}
          <div
            class="relative flex justify-between bg-gray-8 dark:bg-gray-3 rounded-full border border-gray-6 dark:border-gray-3"
          >
            <span
              class="absolute h-full bg-teal dark:bg-gray-7 rounded-full transition-transform ease-out"
              style:width={selectorWidth + '%'}
              style:left={selectedTab * selectorWidth + '%'}
            />
            {#each tabs as tab (tab)}
              <div
                class="flex-1 py-1 text-center relative cursor-pointer"
                class:dark:text-gray-2={selectedTab === tab.value}
                class:text-gray-8={selectedTab === tab.value}
                on:click={() => (selectedTab = tab.value)}
              >
                {tab.label}
              </div>
            {/each}
          </div>
          <HockeyRink
            awayTeam={team}
            homeTeam={team}
            goalEvents={$selectedPlayer.events
              .filter((e) =>
                selectedTab === 0 ? true : e.period === selectedTab,
              )
              .filter((e) => e.type === 'goal')}
            shotEvents={$selectedPlayer.events
              .filter((e) =>
                selectedTab === 0 ? true : e.period === selectedTab,
              )
              .filter((e) => e.type === 'shotSaved' || e.type === 'shotMissed')}
            hitEvents={$selectedPlayer.events
              .filter((e) =>
                selectedTab === 0 ? true : e.period === selectedTab,
              )
              .filter((e) => e.type === 'hit')}
            penaltyEvents={$selectedPlayer.events
              .filter((e) =>
                selectedTab === 0 ? true : e.period === selectedTab,
              )
              .filter((e) => e.type === 'penalty')}
          />
        {/if}
      </div>
    </div>
  </div>
{/if}
